import { useEffect, useState } from "react";
import { getMonolithReports } from "../../api";
import { reportQueryParameters } from "./params/report-params-utils";
import StorageReport from "./components/Storage/StorageReport";
import CasesReport from "./components/Cases/CaseReport";
import EvidenceReport from "./components/Evidence/EvidenceReport";
import QaEntryReport from "./components/QA/Report";
import AcquisitionsReport from "./components/Acquisitions/AcquisitionsReport";
import { Link, useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import { Grid } from "@mui/material";
import { Divider } from "@monolith-forensics/monolith-ui";
import { Helmet } from "react-helmet-async";
import { CATEGORY_MAP } from "./CategoryMap";
import { FprReport } from "./components/FPR/FprReport";
import { TimeEntryReport } from "./components/TimeEntry/TimeEntryReport";
import styled, { useTheme } from "styled-components";
import { Typography } from "@monolith-forensics/monolith-ui";
import { User } from "../Users/types/Users";

interface Report {
  category: string;
  category_id: number;
  created_by: string;
  created_by_id: number;
  created_on: string;
  name: string;
  notifications: string;
  report_id: number;
  report_parameters: {
    end_date: string;
    case_lead: Partial<User>[];
    case_types: string[];
    case_statuses: string[];
    organization: string[];
    start_date: string;
    time_interval: string;
    timestamp_dimension: string;
  };
  type: string;
}

//Metrics to add to report
// Evidence Intake during period
// Evidence Items Released
// Acquisitions created during period
// total acquisition data (stored)
// Acquisition tool composition
// Distinct acquisition total
// Cases Opened During Period
// Cases Closed During Period
// Case Progress Composition
// Case Status Composition
// Case Type Composition
// Case Documents uploaded
// Case Notes Created
// Evidence Type Composition
// Evidence Progress Composition
// Total Storage items assigned
// Storage items assigned during period
// Storage items released during period
// User activity Composition
// Inquiries received during period
// Inquiries converted during period
// Inquiries merged during period
// Custom Attribute Stats

const ReportDetails = styled(({ className }) => {
  const [reportInfo, setReportInfo] = useState<Report | null>(null);
  const { report_id: reportID } = useParams();
  const theme: any = useTheme();
  const [formUpdated, setFormUpdated] = useState(false);

  useEffect(() => {
    setReportInfo(null);
    getMonolithReports({ report_id: reportID }).then((result) => {
      const report = result[0];
      // Legacy reports saved before ENG-1417 can carry stale start/end dates
      // under an unbounded ("All Time"/absent) interval. Strip them once here
      // so every downstream query, the params panel, and the edit form treat
      // the report as truly unbounded (the backend applies any dates present).
      setReportInfo(
        report
          ? {
              ...report,
              report_parameters: reportQueryParameters(
                report.report_parameters
              ),
            }
          : report
      );
    });
  }, [formUpdated]);

  if (!reportInfo) return <Loader />;

  return (
    <div className={className}>
      <Helmet title="Monolith Reports" />
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h3">{reportInfo.name}</Typography>
          <div style={{ color: "slategray", fontSize: "medium" }}>
            Monolith Report
          </div>
        </Grid>
        <div
          style={{
            fontSize: "smaller",
            marginLeft: "auto",
            display: "flex",
            alignItems: "end",
          }}
        >
          <Link to={`/reports`}>
            <span
              style={{
                cursor: "pointer",
                color: theme.palette.primary.main,
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.textDecoration = "underline";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.textDecoration = "none";
              }}
            >
              {"< Back to Reports"}
            </span>
          </Link>
        </div>
      </Grid>
      <Divider style={{ marginTop: "8px", marginBottom: "32px" }} />
      <div>
        {reportInfo && (
          <>
            {reportInfo.category_id === CATEGORY_MAP.CASES && (
              <CasesReport
                reportInfo={reportInfo}
                setFormUpdated={setFormUpdated}
              />
            )}
            {reportInfo.category_id === CATEGORY_MAP.EVIDENCE && (
              <EvidenceReport
                reportInfo={reportInfo}
                setFormUpdated={setFormUpdated}
                formUpdated={undefined}
              />
            )}
            {reportInfo.category_id === CATEGORY_MAP.STORAGE && (
              <StorageReport
                reportInfo={reportInfo}
                setFormUpdated={setFormUpdated}
              />
            )}
            {reportInfo.category_id === CATEGORY_MAP.ACQUISITIONS && (
              <AcquisitionsReport
                reportInfo={reportInfo}
                setFormUpdated={setFormUpdated}
              />
            )}
            {reportInfo.category_id === CATEGORY_MAP.QA_ENTRIES && (
              <QaEntryReport
                reportInfo={reportInfo}
                setFormUpdated={setFormUpdated}
              />
            )}
            {reportInfo.category_id === CATEGORY_MAP.FPR && (
              <FprReport
                reportInfo={reportInfo}
                setFormUpdated={setFormUpdated}
              />
            )}
            {reportInfo.category_id === CATEGORY_MAP.TIME_ENTRIES && (
              <TimeEntryReport
                reportInfo={reportInfo}
                setFormUpdated={setFormUpdated}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
})`
  flex-grow: 1;
  height: 0px;
  overflow-y: auto;
  padding-right: 10px;
`;

export default ReportDetails;
